const institutionComponents = {
  shortName: {},
  institutionNumber: { required: true },
  contactDetails: {},
  mail: {},
};

const orgComponents = {
  shortName: { historical: true },
  description: {},
};

const bestuurRelation = {
  key: 'governingInstitution',
  type: 'GOVERNS',
  ouTypes: new Set(['GOVERNINGINSTITUTION']),
  label: 'Bestuur',
  thisOu: 'to',
  isNeuter: true, // in dutch when a noun is neuter, the article 'het' is used instead of 'de'
  canOnlyHaveOne: true, // can only have one relation of this type (at each point in time!)
  isRequired: true,
};

const parentRel = {
  key: 'parent',
  type: 'IS_PART_OF',
  label: 'Is onderdeel van',
  thisOu: 'from',
  ouTypes: new Set(),
  canOnlyHaveOne: true,
};

const childRel = {
  key: 'children',
  type: 'IS_PART_OF',
  label: 'Onderdelen',
  newLabel: `Voeg onderdeel toe`,
  ouTypes: new Set(),
  thisOu: 'to',
  isOnlyOne: true,
};

const providesServicesTo = { key: 'pSt', type: 'PROVIDES_SERVICES_TO', ouTypes: new Set(['DIENST','VERGADERGROEP','BELEIDSORGAAN']), label: 'Levert diensten aan', thisOu: 'from' };
const receivesServicesFrom = { key: 'recServF', type: 'PROVIDES_SERVICES_TO', ouTypes: new Set(['DIENST','VERGADERGROEP','LEERPLANCOMMISSIE','BELEIDSORGAAN','BISDOM']), label: 'Ontvangt diensten van', thisOu: 'to' };
const isMemberOf = { key: 'isMemberOf', type: 'IS_MEMBER_OF', ouTypes: new Set(['VERGADERGROEP']), label: 'Is lid van', thisOu: 'from' };
const hasMembers = { key: 'hasMembers', type: 'IS_MEMBER_OF', ouTypes: new Set(['DIENST','VERGADERGROEP','BELEIDSORGAAN','BISDOM','EXTERN']), label: 'Heeft als leden', thisOu: 'to' };

/**
 * For now generic components for all OU types are not listed: name, startDate, endDate
 */
/** @type {import('../../../../types').AdminOuTypeConfigMap}*/
export const OU_TYPE_CONFIG = {
  SCHOOL: {
    label: 'officiële school',
    domain: 'sam',
    components: {
      ...institutionComponents,
      relations: [
        bestuurRelation,
        { key: 'sc', type: 'IS_MEMBER_OF', ouTypes: new Set(['SCHOOLCOMMUNITY']), label: 'Scholengemeenschap', thisOu: 'from', swithOnSchoolYear: true, canOnlyHaveOne: true, isRequired: false },
        { key: 'clb', type: 'PROVIDES_SERVICES_TO', ouTypes: new Set(['CLB']), label: 'CLB', thisOu: 'to', canOnlyHaveOne: true, swithOnSchoolYear: true, readOnly: true, isNeuter: true, canOnlyHaveOne: true, isRequired: true },
        { key: 'lrc', type: 'PROVIDES_SERVICES_TO', ouTypes: new Set(['ALGEMEEN_LEERSTEUNCENTRUM']), label: 'Leersteuncenctrum', thisOu: 'to', canOnlyHaveOne: true, readOnly: true, isNeuter: true },
        { key: 'ses', type: 'CAN_ACT_AS', ouTypes: new Set(['SCHOOLENTITY']), label: 'Schoolentiteit', plural: 'Schoolentiteiten', thisOu: 'to', readOnly: true },
      ],
      locations: {},
    }
  },
  GOVERNINGINSTITUTION: {
    isNeuter: true,
    label: 'bestuur',
    domain: 'sam',
    components: {
      ...institutionComponents,
      institutionNumber: { required: false },
      legalForm: {},
      companyNumber: {},
      vatNumber: {},
      relations: [
        { key: 'koepel', type: 'IS_MEMBER_OF', ouTypes: new Set(['ONDERWIJSKOEPEL']), label: 'Koepel', thisOu: 'from', swithOnSchoolYear: true, canOnlyHaveOne: true, isRequired: true },
        { key: 'schools', type: 'GOVERNS', ouTypes: new Set(['SCHOOL']), label: 'School', plural: 'Scholen', thisOu: 'from', isOnlyOne: true, isRequired: true },
        { key: 'otherInstitutions', type: 'GOVERNS', ouTypes: new Set(['BOARDING', 'SCHOOLCOMMUNITY', 'CLB', 'ALGEMEEN_LEERSTEUNCENTRUM', 'SPECIFIEK_LEERSTEUNCENTRUM']), label: 'Instelling', plural: `Internaten, scholengemeenschappen, CLB's en leersteuncentra`, thisOu: 'from', isOnlyOne: true, isRequired: true },
        { key: 'ses', type: 'GOVERNS', ouTypes: new Set(['SCHOOLENTITY']), label: 'Schoolentiteit', plural: 'Schoolentiteiten', thisOu: 'from', readOnly: true, isOnlyOne: true, isRequired: true },
        { key: 'represents', type: 'IS_REPRESENTED_IN', ouTypes: new Set(['GOVERNINGINSTITUTION']), label: 'wordt mede-bestuurd door', thisOu: 'to' },
        { key: 'isRepresentedIn', type: 'IS_REPRESENTED_IN', ouTypes: new Set(['GOVERNINGINSTITUTION']), label: 'is vertegenwoordigd in', thisOu: 'from' },
      ],
    }
  },
  BOARDING: {
    isNeuter: true,
    label: 'internaat',
    domain: 'sam',
    components: {
      ...institutionComponents,
      relations: [
        bestuurRelation,
      ],
    }
  },
  CLB: {
    isNeuter: true,
    label: 'clb',
    domain: 'sam',
    components: {
      ...institutionComponents,
      relations: [
        bestuurRelation,
        { key: 'schools', type: 'PROVIDES_SERVICES_TO', ouTypes: new Set(['SCHOOL']), label: 'School', plural: 'Scholen', thisOu: 'from', isOnlyOne: true, isRequired: true },
      ],
    }
  },
  SCHOOLCOMMUNITY: {
    label: 'scholengemeenschap',
    domain: 'sam',
    components: {
      ...institutionComponents,
      relations: [
        bestuurRelation,
        { key: 'schools', type: 'IS_MEMBER_OF', ouTypes: new Set(['SCHOOL']), label: 'School', plural: 'Scholen', thisOu: 'to', isOnlyOne: true, isRequired: false },
      ],
    }
  },
  ALGEMEEN_LEERSTEUNCENTRUM: {
    isNeuter: true,
    label: 'algemeen leersteuncentrum',
    domain: 'sam',
    components: {
      ...institutionComponents,
      relations: [
        bestuurRelation,
        { key: 'specLcn', type: 'PROVIDES_SERVICES_TO', ouTypes: new Set(['SPECIFIEK_LEERSTEUNCENTRUM']), label: 'Specifiek leersteuncentrum', thisOu: 'to', readOnly: true },
        { key: 'schools', type: 'PROVIDES_SERVICES_TO', ouTypes: new Set(['SCHOOL']), label: 'School', plural: 'Scholen', thisOu: 'from', isOnlyOne: true, readOnly: true },
      ],
    }
  },
  SPECIFIEK_LEERSTEUNCENTRUM: {
    isNeuter: true,
    label: 'specifiek leersteuncentrum',
    domain: 'sam',
    components: {
      ...institutionComponents,
      relations: [
        bestuurRelation,
        { key: 'algLcn', type: 'PROVIDES_SERVICES_TO', ouTypes: new Set(['ALGEMEEN_LEERSTEUNCENTRUM']), label: 'Algemeen leersteuncentrum', thisOu: 'from', readOnly: true },
      ],
    }
  },
  DIENST: {
    label: 'dienst',
    domain: 'kathondvla',
    components: {
      shortName: { historical: true },
      callName: {},
      mail: { noSeat: true},
      relations: [
        { ...parentRel, ouTypes: new Set(['DIENST']) },
        { ...childRel, ouTypes: new Set(['DIENST']) },
        providesServicesTo,
        receivesServicesFrom,
        isMemberOf,
        { key: 'isGovernedBy', type: 'GOVERNS', ouTypes: new Set(['BELEIDSORGAAN']), label: 'Wordt bestuurd door', thisOu: 'to' },
      ],
    }
  },
  VERGADERGROEP: {
    label: 'vergadergroep',
    domain: 'kathondvla',
    components: {
      ...orgComponents,
      relations: [
        { ...parentRel, ouTypes: new Set(['VERGADERGROEP']) },
        { ...childRel, ouTypes: new Set(['VERGADERGROEP','LEERPLANCOMMISSIE','BELEIDSORGAAN']) },
        providesServicesTo,
        receivesServicesFrom,
        { ...isMemberOf, ouTypes: new Set(['VERGADERGROEP','LERARENNETWERK']) },
        hasMembers,
      ],
    }
  },
  LEERPLANCOMMISSIE: {
    label: 'leerplancommissie',
    domain: 'kathondvla',
    components: {
      ...orgComponents,
      relations: [
        providesServicesTo,
        { ...parentRel, ouTypes: new Set(['VERGADERGROEP']) },
      ],
    }
  },
  BELEIDSORGAAN: {
    isNeuter: true,
    label: 'beleidsorgaan',
    domain: 'kathondvla',
    components: {
      ...orgComponents,
      contactDetails: {},
      mail: { noSeat: true},
      relations: [
        { key: 'bestuurt', type: 'GOVERNS', ouTypes: new Set(['DIENST','EXTERN']), label: 'Bestuurt', thisOu: 'from' },
        { ...parentRel, ouTypes: new Set(['BELEIDSORGAAN','VERGADERGROEP']) },
        { ...childRel, ouTypes: new Set(['BELEIDSORGAAN']) },
        providesServicesTo,
        receivesServicesFrom,
        isMemberOf,
      ],
    }
  },
  LERARENNETWERK: {
    isNeuter: true,
    label: 'lerarennetwerk',
    domain: 'kathondvla',
    components: {
      ...orgComponents,
      relations: [
        { ...hasMembers, ouTypes: new Set(['VERGADERGROEP']) },
      ]
    }
  },
  PROJECTGROEP: {
    label: 'projectgroep',
    domain: 'kathondvla',
    components: {
      callName: {},
      ...orgComponents,
      relations: [
        { ...parentRel, ouTypes: new Set(['PROJECTGROEP']) },
        { ...childRel, ouTypes: new Set(['PROJECTGROEP']) },
      ],
    }
  },
  EXTERN: {
    label: 'externe organisatie',
    domain: 'kathondvla',
    allowInternational: true,
    components: {
      callName: {},
      legalForm: {},
      companyNumber: {},
      vatNumber: {},
      ...orgComponents,
      contactDetails: {},
      mail: { noSeat: true},
      relations: [
        { ...parentRel, ouTypes: new Set(['EXTERN']) },
        { ...childRel, ouTypes: new Set(['EXTERN']) },
        { key: 'isMemberOf', type: 'IS_MEMBER_OF', ouTypes: new Set(['VERGADERGROEP']), label: 'Is lid van', thisOu: 'from' },
        { key: 'isGovernedBy', type: 'GOVERNS', ouTypes: new Set(['BELEIDSORGAAN']), label: 'Wordt bestuurd door', thisOu: 'to' },
      ],
    }
  },
  BISDOM: {
    isNeuter: true,
    label: 'bisdom',
    domain: 'kathondvla',
    components: {
      ...orgComponents,
      contactDetails: {},
      mail: { noSeat: true},
      relations: [
        { ...parentRel, ouTypes: new Set(['BISDOM']) },
        { ...childRel, ouTypes: new Set(['BISDOM']) },
        providesServicesTo,
        isMemberOf,
      ],
    }
  },
  POLITIEK_ORGAAN: {
    isNeuter: true,
    label: 'politiek orgaan',
    domain: 'kathondvla',
    components: {
      ...orgComponents,
      contactDetails: {},
      mail: { noSeat: true},
      relations: [],
    }
  },
  ONDERWIJSKOEPEL: {
    label: 'onderwijskoepel',
    domain: 'sam',
    components: {
      ...institutionComponents,
      companyNumber: {},
      vatNumber: {},
    },
  }
};